import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import "./App.css";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import ImpressumPage from "@/pages/ImpressumPage";
import AGBPage from "@/pages/AGBPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import CreateProjectPage from "@/pages/CreateProjectPage";
import AnbieterPage from "@/pages/AnbieterPage";
import AnbieterDetailPage from "@/pages/AnbieterDetailPage";
import MeinWaldPage from "@/pages/MeinWaldPage";
import MessagesPage from "@/pages/MessagesPage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import FallanalysePage from "@/pages/FallanalysePage";
import SchnellCheckPage from "@/pages/SchnellCheckPage";
import SammelfallPage from "@/pages/SammelfallPage";
import DienstleisterPage from "@/pages/DienstleisterPage";
import AdminPage from "@/pages/AdminPage";

// WaldBerater is now inline in LandingPage, not a global overlay

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="animate-pulse text-primary">Laden...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/anmelden" state={{ from: location.pathname }} replace />;
  }
  
  return children;
};

// Onboarding Check Route
const OnboardingCheck = ({ children }) => {
  const { user } = useAuth();
  
  if (user && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Public Only Route (redirect if logged in, handle entwurf assignment)
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="animate-pulse text-primary">Laden...</div>
      </div>
    );
  }
  
  if (user) {
    // Entwurf-Zuordnung: wenn User auf /registrieren?entwurf=XXX oder /anmelden?entwurf=XXX landet
    const params = new URLSearchParams(window.location.search);
    const entwurfId = params.get('entwurf');
    if (entwurfId && user.id) {
      import("@/lib/api").then(({ fallanalyseAPI }) => {
        fallanalyseAPI.projektEntwurfZuordnen(entwurfId, user.id).catch(() => {});
      });
    }
    const from = location.state?.from;
    const defaultRoute = from || (user.role === 'anbieter' ? '/projekte' : '/dashboard');
    return <Navigate to={defaultRoute} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/anmelden" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/registrieren" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
      <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
      <Route path="/passwort-reset" element={<ResetPasswordPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/agb" element={<AGBPage />} />
      <Route path="/schnell-check" element={<SchnellCheckPage />} />
      <Route path="/sammelfall/:id" element={<SammelfallPage />} />
      <Route path="/fuer-dienstleister" element={<DienstleisterPage />} />
      
      {/* Onboarding */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><OnboardingCheck><DashboardPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/projekte" element={
        <ProtectedRoute><OnboardingCheck><ProjectsPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/projekte/neu" element={
        <ProtectedRoute><OnboardingCheck><CreateProjectPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/projekte/:id" element={
        <ProtectedRoute><OnboardingCheck><ProjectDetailPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/anbieter" element={
        <ProtectedRoute><OnboardingCheck><AnbieterPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/anbieter/:id" element={
        <ProtectedRoute><OnboardingCheck><AnbieterDetailPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/mein-wald" element={
        <ProtectedRoute><OnboardingCheck><MeinWaldPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/nachrichten" element={
        <ProtectedRoute><OnboardingCheck><MessagesPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/nachrichten/:userId" element={
        <ProtectedRoute><OnboardingCheck><MessagesPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/profil" element={
        <ProtectedRoute><OnboardingCheck><ProfilePage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/einstellungen" element={
        <ProtectedRoute><OnboardingCheck><SettingsPage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/fallanalyse" element={
        <ProtectedRoute><OnboardingCheck><FallanalysePage /></OnboardingCheck></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute><AdminPage /></ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
